export type Role = 'user' | 'admin' | 'super_admin'

export const ROLES = {
  USER: 'user' as Role,
  ADMIN: 'admin' as Role,
  SUPER_ADMIN: 'super_admin' as Role,
}

export function isAdmin(role?: Role): boolean {
  return role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN
}

export function isSuperAdmin(role?: Role): boolean {
  return role === ROLES.SUPER_ADMIN
}

export type AuthProvider = 'local' | 'microsoft'

export type SupportChannelType = 'whatsapp' | 'teams' | 'slack'

/** Canal de atendimento da empresa — alimenta o botão flutuante de suporte. */
export type SupportChannel = {
  type: SupportChannelType
  url: string
  label?: string | null
}

export type User = {
  id: string
  name: string
  email: string
  avatar?: string
  role: Role
  authProvider: AuthProvider
  companyId?: string | null
  createdAt: string
  updatedAt: string
  supportChannel?: SupportChannel | null
}

/**
 * Checks if an admin can manage (edit/delete) a course-based resource.
 * Super admins can manage everything. Regular admins cannot manage
 * global courses (companyId: null) — those are created by super_admin.
 */
export function canManageCourseResource(userRole?: Role, courseCompanyId?: string | null): boolean {
  if (isSuperAdmin(userRole)) return true
  return !!courseCompanyId
}

/**
 * Checks if an admin can manage (edit/delete) a drop.
 * Super admins can manage everything. Regular admins can only manage
 * drops from their own company. Global drops (companyId null) are super_admin only.
 */
export function canManageDrop(
  userRole?: Role,
  userCompanyId?: string | null,
  dropCompanyId?: string | null
): boolean {
  if (isSuperAdmin(userRole)) return true
  if (!dropCompanyId) return false
  return userCompanyId === dropCompanyId
}

export type LoginRequest = {
  email: string
  password: string
}

export type RegisterRequest = {
  name: string
  email: string
  password: string
  confirmPassword: string
  termsAccepted: boolean
  couponCode?: string
}

export type AuthResponse = {
  user: User
  token: string
}

// ── 2FA ──────────────────────────────────────────────────────────────────────
export type TwoFactorMethod = 'email' | 'totp'

/** Resposta do login quando o 2FA está ativo (JWT só sai após o /verify). */
export type TwoFactorChallenge = {
  twoFactorRequired: true
  challengeId: string
  method: TwoFactorMethod
}

/** Login pode devolver a sessão OU um desafio de 2FA. */
export type LoginResult = AuthResponse | TwoFactorChallenge

export type TwoFactorStatus = {
  enabled: boolean
  method: TwoFactorMethod | null
}

export type TwoFactorSetupResponse = {
  method: TwoFactorMethod
  // presentes só pro método totp:
  secret?: string
  otpauthUrl?: string
  qrDataUrl?: string
}

export type TwoFactorEnableResponse = {
  enabled: boolean
  method: TwoFactorMethod
  backupCodes?: string[]
}

export function isTwoFactorChallenge(r: LoginResult): r is TwoFactorChallenge {
  return (r as TwoFactorChallenge).twoFactorRequired === true
}

// ── Verificação de e-mail (ativação de conta) ────────────────────────────────

/** Resposta do cadastro local: conta criada, aguardando ativação por e-mail. */
export type EmailVerificationPending = {
  emailVerificationRequired: true
  email: string
}

/** O cadastro pode devolver sessão (ex.: futuro) OU pendência de ativação. */
export type RegisterResult = AuthResponse | EmailVerificationPending

export function isEmailVerificationPending(r: RegisterResult): r is EmailVerificationPending {
  return (r as EmailVerificationPending).emailVerificationRequired === true
}

/** Code retornado pelo login quando a conta local ainda não foi ativada. */
export const EMAIL_NOT_VERIFIED_CODE = 'EMAIL_NOT_VERIFIED'

export type TokenPayload = {
  userId: string
  role: Role
  iat: number
  exp: number
}
